import React, { useState, useMemo, useEffect } from 'react';
import { Routes, Route, Outlet, useOutletContext, useLocation, useNavigate, Link } from 'react-router-dom';
import { Search, X, Calendar, Factory, Clapperboard, Youtube, Image as ImageIcon, ChevronRight, Mail, Send, Gamepad2 } from 'lucide-react';

// 1. IMPORT DATA
import { PUPPETS_DATA } from './data/puppets';
import { GAMES_DATA } from './data/games';

// 2. IMPORT COMPONENTS
import GameCard from './components/GameCard';
import GamesPage from './pages/GamesPage';

// Helper to create URL slug from puppet name
const getPuppetSlug = (puppet) => {
  return puppet.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
};

// Helper to find puppet from URL path
const getPuppetFromPath = (pathname) => {
  const match = pathname.match(/^\/puppet\/([^/]+)/);
  if (!match) return null;
  const slug = match[1].toLowerCase();
  return PUPPETS_DATA.find(p => getPuppetSlug(p) === slug) || null;
};

// --- AUXILIARY COMPONENTS ---

// Gear Logo
const Logo = () => (
  <img 
    src="/logo.png" 
    alt="Mario's Puppets Logo" 
    className="w-12 h-12 object-contain" 
  />
);

// --- MAIN COMPONENTS ---

const PuppetCard = ({ puppet, onClick }) => {
  // Color mapping for placeholders
  const getColorClass = (colorName) => {
    const colors = {
      blue: "bg-blue-200 text-blue-700",
      indigo: "bg-indigo-200 text-indigo-700",
      pink: "bg-pink-200 text-pink-700",
      gray: "bg-gray-200 text-gray-700",
      sky: "bg-sky-200 text-sky-700",
      cyan: "bg-cyan-200 text-cyan-700",
      yellow: "bg-yellow-200 text-yellow-700",
      rose: "bg-rose-200 text-rose-700",
      fuchsia: "bg-fuchsia-200 text-fuchsia-700",
      slate: "bg-slate-200 text-slate-700",
      zinc: "bg-zinc-200 text-zinc-700",
    };
    return colors[colorName] || "bg-gray-200 text-gray-700";
  };

  return (
    <div 
      onClick={() => onClick(puppet)}
      className="group bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden border border-slate-100 transform hover:-translate-y-1"
    >
      <div className={`aspect-[3/4] w-full ${getColorClass(puppet.images.front)} flex items-center justify-center relative overflow-hidden`}>
        {puppet.images.front?.startsWith('/') ? (
          <img 
            src={puppet.images.front} 
            alt={puppet.name}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" 
          />
        ) : (
          <ImageIcon size={64} className="opacity-50 group-hover:scale-110 transition-transform duration-500" />
        )}
        <span className="absolute bottom-2 right-2 text-xs bg-white/80 px-2 py-1 rounded-full font-medium shadow-sm">
           View Details
        </span>
      </div>
      <div className="p-5">
        <h3 className="text-xl font-bold text-slate-800 mb-2 group-hover:text-rose-600 transition-colors">
          {puppet.name}
        </h3>
        <p className="text-slate-500 text-sm line-clamp-2">
          {puppet.description}
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {puppet.appearances.slice(0, 2).map((app, idx) => (
            <span key={idx} className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-md">
              {app}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

const PuppetModal = ({ puppet, onClose }) => {
  const [activeImage, setActiveImage] = useState(puppet.images.front);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  if (!puppet) return null;

  // Combine front image with gallery
  const allImages = [puppet.images.front, ...(puppet.images.gallery || [])];

  const getColorClass = (colorName) => {
     const map = {
        blue: "bg-blue-100", indigo: "bg-indigo-100", pink: "bg-pink-100", 
        gray: "bg-gray-100", sky: "bg-sky-100", cyan: "bg-cyan-100",
        yellow: "bg-yellow-100", rose: "bg-rose-100", fuchsia: "bg-fuchsia-100",
        slate: "bg-slate-100", zinc: "bg-zinc-100"
     };
     return map[colorName] || "bg-gray-100";
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl flex flex-col md:flex-row relative"
        onClick={(e) => e.stopPropagation()}
      >
        
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 bg-white/50 hover:bg-white rounded-full transition-colors"
        >
          <X size={24} className="text-slate-800" />
        </button>

        {/* Left Column: Gallery */}
        <div className="w-full md:w-1/2 bg-slate-50 p-6 flex flex-col gap-4">
          <div className={`aspect-[3/4] rounded-xl shadow-inner flex items-center justify-center ${getColorClass(activeImage)} transition-colors duration-300 relative overflow-hidden`}>
             {activeImage?.startsWith('/') ? (
               <img 
                 src={activeImage} 
                 alt={puppet.name}
                 className="w-full h-full object-cover" 
               />
             ) : (
               <ImageIcon size={120} className="text-slate-400 opacity-60" />
             )}
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {allImages.map((img, idx) => (
              <button 
                key={idx}
                onClick={() => setActiveImage(img)}
                className={`h-20 w-16 rounded-lg flex-shrink-0 border-2 ${activeImage === img ? 'border-rose-500' : 'border-transparent'} ${getColorClass(img)} flex items-center justify-center overflow-hidden`}
              >
                {img?.startsWith('/') ? (
                  <img 
                    src={img} 
                    alt={`${puppet.name} thumbnail ${idx}`}
                    className="w-full h-full object-cover" 
                  />
                ) : (
                  <ImageIcon size={24} className="opacity-40" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Right Column: Information */}
        <div className="w-full md:w-1/2 p-6 md:p-8 flex flex-col">
          <h2 className="text-3xl font-bold text-slate-800 mb-1">{puppet.name}</h2>
          <span className="inline-block text-rose-500 font-medium mb-4">{puppet.manufacturer}</span>
          
          <div className="space-y-4 flex-grow">
            {puppet.description && (
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-100 text-amber-900 text-sm leading-relaxed">
                {puppet.description}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
               <div className="flex items-start gap-2">
                 <Calendar size={18} className="text-slate-400 mt-0.5" />
                 <div>
                   <p className="text-xs text-slate-400 uppercase font-bold">Acquisition</p>
                   <p className="text-slate-700 font-medium">{puppet.acquisitionDate}</p>
                 </div>
               </div>
               <div className="flex items-start gap-2">
                 <Factory size={18} className="text-slate-400 mt-0.5" />
                 <div>
                   <p className="text-xs text-slate-400 uppercase font-bold">Manufacturer</p>
                   <p className="text-slate-700 font-medium">{puppet.manufacturer}</p>
                 </div>
               </div>
            </div>

            {puppet.appearances && puppet.appearances.length > 0 && puppet.appearances[0] !== "" && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Clapperboard size={18} className="text-slate-400" />
                  <p className="text-xs text-slate-400 uppercase font-bold">Appearances</p>
                </div>
                <ul className="list-disc list-inside text-sm text-slate-700 space-y-1 ml-1">
                  {puppet.appearances.map((app, i) => (
                    <li key={i}>{app}</li>
                  ))}
                </ul>
              </div>
            )}

            {puppet.videos && puppet.videos.length > 0 && (
              <div className="mt-4 pt-4 border-t border-slate-100">
                <h4 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
                  <Youtube size={18} className="text-rose-500" />
                  Mario's World Youtube videos
                </h4>
                <div className="flex flex-col gap-2">
                  {puppet.videos.map((vid, i) => (
                    <a 
                      key={i} 
                      href={vid.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-rose-50 text-sm text-slate-700 hover:text-rose-700 transition-colors border border-slate-200 hover:border-rose-200 group"
                    >
                      <span>{vid.title}</span>
                      <ChevronRight size={16} className="text-slate-400 group-hover:text-rose-500" />
                    </a>
                  ))}
                  <a 
                    href="https://www.youtube.com/@MariosWorld" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-xs text-rose-500 hover:text-rose-700 font-medium mt-1 flex items-center gap-1 self-end"
                  >
                    View Youtube Channel <ChevronRight size={12} />
                  </a>
                </div>
              </div>
            )}
          </div>
          
          <div className="mt-8 pt-6 border-t border-slate-100 flex justify-between items-center">
             <span className="text-xs text-slate-400">ID: #{puppet.id}</span>
             <button onClick={onClose} className="text-slate-600 font-medium hover:text-slate-900">
               Close Details
             </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ContactModal = ({ onClose }) => {
  const [status, setStatus] = useState('idle'); // idle, submitting, success, error

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');

    const form = e.target;
    const formData = new FormData(form);

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-md shadow-2xl relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 bg-slate-100 hover:bg-slate-200 rounded-full transition-colors"
        >
          <X size={20} className="text-slate-600" />
        </button>

        <div className="p-6 md:p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-rose-100 rounded-full">
              <Mail size={24} className="text-rose-500" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-800">Contact</h2>
              <p className="text-sm text-slate-500">Get in touch with Mario</p>
            </div>
          </div>

          {status === 'success' ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Send size={32} className="text-green-500" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">Message Sent!</h3>
              <p className="text-slate-500 mb-6">Thanks for reaching out. I'll get back to you soon.</p>
              <button
                onClick={onClose}
                className="px-6 py-2 bg-slate-800 text-white rounded-full font-medium hover:bg-slate-700 transition-colors"
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <input type="hidden" name="access_key" value="aba4e4ec-045c-4a6f-9b13-87fd8f1f0a12" />
              <input type="checkbox" name="botcheck" className="hidden" />

              <div>
                <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-1">Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-rose-500 transition-all"
                  placeholder="Your name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-rose-500 transition-all"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-1">Message</label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={4}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-rose-500 transition-all resize-none"
                  placeholder="Your message..."
                />
              </div>

              {status === 'error' && (
                <p className="text-red-500 text-sm">Something went wrong. Please try again.</p>
              )}

              <button
                type="submit"
                disabled={status === 'submitting'}
                className="w-full py-3 bg-rose-500 text-white rounded-lg font-medium hover:bg-rose-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {status === 'submitting' ? (
                  'Sending...'
                ) : (
                  <>
                    <Send size={18} />
                    Send Message
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

// The three most recently added games, newest first.
const featuredGames = [...GAMES_DATA].sort((a, b) => b.id - a.id).slice(0, 3);


// --- LAYOUT ---

// Chrome shared by every page: header, announcement banner and footer.
// The search box lives in the header but only filters the puppet gallery,
// so its state is handed down to the page through the outlet context.
function Layout() {
  const [searchTerm, setSearchTerm] = useState("");
  const [showBanner, setShowBanner] = useState(() => !document.cookie.includes('hideBanner=1'));
  const location = useLocation();
  const showSearch = location.pathname !== '/games';

  const dismissBanner = () => {
    const expires = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toUTCString();
    document.cookie = `hideBanner=1; expires=${expires}; path=/`;
    setShowBanner(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900">

      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between py-4 gap-4">
            
            {/* Logo and Title */}
            <Link to="/" className="flex items-center gap-3 group">
              <Logo />
              <div>
                <h1 className="text-2xl font-extrabold tracking-tight text-slate-800">
                  Mario's <span className="text-rose-500">Puppets</span>
                </h1>
                <p className="text-xs text-slate-500 font-medium tracking-wide uppercase">
                  Private Collection & Archive
                </p>
              </div>
            </Link>

            {/* Search Bar & Socials */}
            <div className="flex items-center gap-4 w-full md:w-auto">
              {showSearch && (
              <div className="relative w-full md:w-96 group">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search size={18} className="text-slate-400 group-focus-within:text-rose-500 transition-colors" />
                </div>
                <input
                  type="text"
                  placeholder="Search by name, show, or manufacturer..."
                  className="block w-full pl-10 pr-3 py-2 border border-slate-200 rounded-full leading-5 bg-slate-50 text-slate-900 placeholder-slate-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-rose-500 focus:border-rose-500 transition-all shadow-sm"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              )}
              <Link
                to="/games"
                className="flex items-center gap-2 px-3 py-2 text-slate-600 hover:text-rose-600 transition-colors bg-white border border-slate-200 rounded-full shadow-sm hover:border-rose-200 font-medium whitespace-nowrap"
                title="Play the games"
              >
                <Gamepad2 size={20} className="text-rose-500" />
                <span className="hidden sm:inline">Games</span>
                <span className="text-xs bg-rose-100 text-rose-600 px-2 py-0.5 rounded-full font-bold">
                  {GAMES_DATA.length}
                </span>
              </Link>
              <a 
                href="https://www.youtube.com/@MariosWorld" 
                target="_blank" 
                rel="noopener noreferrer"
                className="p-2 text-slate-400 hover:text-rose-600 transition-colors bg-white border border-slate-200 rounded-full shadow-sm hover:border-rose-200"
                title="Watch Mario's World on YouTube"
              >
                <Youtube size={20} />
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Announcement Banner */}
      {showBanner && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
          <div className="relative bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
            <button
              onClick={dismissBanner}
              className="absolute top-2 right-2 p-1 text-slate-400 hover:text-slate-600 transition-colors"
              aria-label="Close"
            >
              <X size={16} />
            </button>
            <a
              href="https://www.babyainstein.net"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-5 group"
            >
              <img src="/baby-ainstein-logo.jpg" alt="Baby AInstein" className="h-16 w-auto rounded-lg flex-shrink-0" />
              <div className="flex-grow">
                <p className="text-lg font-bold text-slate-800 group-hover:text-rose-600 transition-colors">Baby AInstein</p>
                <p className="text-sm text-slate-500">Visit our new site — AI-powered Baby Einstein videos!</p>
              </div>
            </a>
          </div>
        </div>
      )}

      <Outlet context={{ searchTerm, setSearchTerm }} />

      <footer className="bg-white border-t border-slate-200 py-10 mt-12">
        <div className="max-w-7xl mx-auto px-4 flex flex-col items-center gap-4">
          <div className="flex items-center gap-3">
            <a
              href="https://www.youtube.com/@MariosWorld"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-slate-600 hover:text-rose-600 transition-colors font-medium border border-slate-200 px-4 py-2 rounded-full hover:bg-rose-50 hover:border-rose-200"
            >
              <Youtube size={20} className="text-rose-500" />
              <span>YouTube</span>
            </a>
            <Link
              to="/contact"
              state={{ fromHome: true }}
              className="flex items-center gap-2 text-slate-600 hover:text-rose-600 transition-colors font-medium border border-slate-200 px-4 py-2 rounded-full hover:bg-rose-50 hover:border-rose-200"
            >
              <Mail size={20} className="text-rose-500" />
              <span>Contact</span>
            </Link>
            <a
              href="https://www.babyainstein.net"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-slate-600 hover:text-rose-600 transition-colors font-medium border border-slate-200 px-4 py-2 rounded-full hover:bg-rose-50 hover:border-rose-200"
            >
              <img src="/baby-ainstein-logo.jpg" alt="Baby AInstein" className="h-5 w-auto" />
              <span>Baby AInstein</span>
            </a>
          </div>
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} Mario's World. Made with ❤️ for collectors.
          </p>
        </div>
      </footer>

    </div>
  );
}


// --- PAGES ---

function HomePage() {
  const { searchTerm, setSearchTerm } = useOutletContext();
  const location = useLocation();
  const navigate = useNavigate();

  const showContact = location.pathname === '/contact';
  const selectedPuppet = useMemo(() => getPuppetFromPath(location.pathname), [location.pathname]);

  // Redirect to home if puppet URL is invalid
  useEffect(() => {
    if (location.pathname.startsWith('/puppet/') && !selectedPuppet) {
      navigate('/', { replace: true });
    }
  }, [location.pathname, selectedPuppet, navigate]);

  const handlePuppetSelect = (puppet) => {
    navigate(`/puppet/${getPuppetSlug(puppet)}`, { state: { fromHome: true } });
  };

  const handleCloseModal = () => {
    if (location.state?.fromHome) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  // Puppet filtering
  const filteredPuppets = PUPPETS_DATA.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.appearances.some(app => app.toLowerCase().includes(searchTerm.toLowerCase())) ||
    p.manufacturer.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Games teaser: the three newest, with the full list one click away. */}
        {GAMES_DATA.length > 0 && (
          <section className="mb-12">
            <div className="mb-6 flex items-center justify-between gap-4">
              <h2 className="text-lg font-bold text-slate-700 flex items-center gap-2">
                <Gamepad2 size={20} className="text-rose-500" />
                Games
              </h2>
              <Link
                to="/games"
                className="flex items-center gap-1 text-sm font-medium text-rose-500 hover:text-rose-600 hover:underline whitespace-nowrap"
              >
                See all {GAMES_DATA.length} games
                <ChevronRight size={16} />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredGames.map(game => (
                <GameCard key={game.id} game={game} />
              ))}
            </div>
          </section>
        )}

        {/* Results and Info */}
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-700">
            Puppet Gallery
          </h2>
          <span className="text-sm bg-slate-200 text-slate-600 px-3 py-1 rounded-full font-medium">
            {filteredPuppets.length} {filteredPuppets.length === 1 ? 'Puppet' : 'Puppets'}
          </span>
        </div>

        {/* Puppet Grid */}
        {filteredPuppets.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredPuppets.map(puppet => (
              <PuppetCard 
                key={puppet.id} 
                puppet={puppet} 
                onClick={handlePuppetSelect} 
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-20 bg-white rounded-xl border border-dashed border-slate-300">
            <p className="text-slate-400 text-lg">No puppets found matching "{searchTerm}"</p>
            <button 
              onClick={() => setSearchTerm("")}
              className="mt-4 text-rose-500 font-medium hover:underline"
            >
              Clear search
            </button>
          </div>
        )}

      </main>

      {/* Modal / Detail */}
      {selectedPuppet && (
        <PuppetModal
          puppet={selectedPuppet}
          onClose={handleCloseModal}
        />
      )}

      {/* Contact Modal */}
      {showContact && (
        <ContactModal onClose={() => location.state?.fromHome ? navigate(-1) : navigate('/')} />
      )}

    </>
  );
}


// --- APP ---

export default function App() {
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/puppet/:slug" element={<HomePage />} />
        <Route path="/contact" element={<HomePage />} />
        <Route path="/games" element={<GamesPage />} />
      </Route>
    </Routes>
  );
}
